/**
 * Placeholder thumbnail for the Reports A La Carte tiles.
 *
 * This is a small art-deco-flavoured SVG that hints at the printed output
 * without being a real preview of any one template. R4 will replace this
 * with a registry-driven mini-render of whichever template is selected.
 *
 * Aspect ratio matches A6 portrait (105×148mm = roughly 0.71:1) so the
 * tile shape stays correct when the real previews drop in.
 */

interface ReportThumbnailProps {
  variant: "pullman-menu-cards" | "travel-and-treats" | "carriage-labels";
}

const THUMB_LABELS: Record<ReportThumbnailProps["variant"], string> = {
  "pullman-menu-cards": "Pullman Menu Cards thumbnail",
  "travel-and-treats": "Travel and Treats thumbnail",
  "carriage-labels": "Carriage Labels thumbnail",
};

export function ReportThumbnail({ variant }: ReportThumbnailProps) {
  // All three reports are live, so use the "available" (warm) palette.
  const palette = {
    paper: "#fbf6e8",
    ink: "#1f4d3f",
    accent: "#bb8e3a",
    muted: "#9a7d44",
  };

  return (
    <svg
      viewBox="0 0 105 148"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={THUMB_LABELS[variant]}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* paper */}
      <rect x="0" y="0" width="105" height="148" fill={palette.paper} />

      {/* outer art deco frame */}
      <rect
        x="6"
        y="6"
        width="93"
        height="136"
        fill="none"
        stroke={palette.accent}
        strokeWidth="0.6"
      />
      <rect
        x="9"
        y="9"
        width="87"
        height="130"
        fill="none"
        stroke={palette.accent}
        strokeWidth="0.3"
      />

      {/* corner chevrons */}
      {[
        { x: 6, y: 6, rotate: 0 },
        { x: 99, y: 6, rotate: 90 },
        { x: 99, y: 142, rotate: 180 },
        { x: 6, y: 142, rotate: 270 },
      ].map((c, i) => (
        <g key={i} transform={`translate(${c.x} ${c.y}) rotate(${c.rotate})`}>
          <path
            d="M 0 0 L 12 0 L 6 6 Z"
            fill={palette.accent}
            opacity="0.85"
          />
          <path
            d="M 2 8 L 8 8 L 5 11 Z"
            fill={palette.accent}
            opacity="0.55"
          />
        </g>
      ))}

      {/* arched header */}
      <path
        d="M 18 22 Q 52.5 8 87 22 L 87 32 L 18 32 Z"
        fill="none"
        stroke={palette.ink}
        strokeWidth="0.7"
      />
      <text
        x="52.5"
        y="20"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="5.4"
        fontWeight="600"
        letterSpacing="0.6"
        fill={palette.ink}
      >
        NORTH YORKSHIRE
      </text>
      <text
        x="52.5"
        y="28"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="4.6"
        fontWeight="500"
        letterSpacing="0.4"
        fill={palette.ink}
      >
        MOORS RAILWAY
      </text>

      {/* divider */}
      <line
        x1="20"
        y1="38"
        x2="85"
        y2="38"
        stroke={palette.muted}
        strokeWidth="0.5"
      />
      <circle cx="52.5" cy="38" r="1.5" fill={palette.accent} />

      {/* mock content lines — labels + values */}
      {[
        { y: 50, label: "Train" },
        { y: 64, label: "Name" },
        { y: 78, label: "Coach" },
      ].map((row) => (
        <g key={row.y}>
          <text
            x="18"
            y={row.y}
            fontFamily="Georgia, serif"
            fontSize="3.2"
            fill={palette.muted}
            letterSpacing="0.3"
          >
            {row.label}
          </text>
          <line
            x1="18"
            y1={row.y + 3.5}
            x2="87"
            y2={row.y + 3.5}
            stroke={palette.muted}
            strokeWidth="0.25"
          />
        </g>
      ))}

      {/* menus block */}
      <text
        x="18"
        y="94"
        fontFamily="Georgia, serif"
        fontSize="3.6"
        fontWeight="600"
        fill={palette.ink}
        letterSpacing="0.3"
      >
        Menus
      </text>
      {[100, 106, 112, 118, 124].map((y, i) => (
        <line
          key={y}
          x1="18"
          y1={y}
          x2={i === 4 ? 70 : 87}
          y2={y}
          stroke={palette.muted}
          strokeWidth="0.25"
          opacity="0.7"
        />
      ))}

      {/* footer rosette */}
      <g transform="translate(52.5 134)">
        <circle r="3.5" fill="none" stroke={palette.accent} strokeWidth="0.4" />
        <circle r="1.4" fill={palette.accent} opacity="0.7" />
        {[0, 60, 120, 180, 240, 300].map((deg) => (
          <line
            key={deg}
            x1="0"
            y1="0"
            x2="0"
            y2="-3.5"
            stroke={palette.accent}
            strokeWidth="0.3"
            transform={`rotate(${deg})`}
          />
        ))}
      </g>
    </svg>
  );
}
